/**
 * CasaMapa anonymous / local buyer state (public listing IDs only).
 * Namespace: client-view:{slug}:*
 */

export const CASAMAPA_COMPARE_MAX = 4;
export const CASAMAPA_COMPARE_MIN = 2;
export const CASAMAPA_RECENT_MAX = 20;
export const CASAMAPA_BUYER_STATE_EVENT = "casamapa-buyer-state-changed";

function key(slug, suffix) {
  return `client-view:${String(slug || "").trim().toLowerCase()}:${suffix}`;
}

function readJson(storageKey, fallback) {
  if (typeof window === "undefined") {
    return fallback;
  }
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(storageKey, value) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    /* quota */
  }
}

function emit(slug, detail = {}) {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(CASAMAPA_BUYER_STATE_EVENT, { detail: { slug, ...detail } })
  );
}

export function normalizeIdList(ids, max) {
  if (!Array.isArray(ids)) {
    return [];
  }
  return [...new Set(ids.map((id) => String(id || "").trim()).filter(Boolean))].slice(0, max);
}

export function loadCasaMapaCompareIds(slug) {
  return normalizeIdList(readJson(key(slug, "compare"), []), CASAMAPA_COMPARE_MAX);
}

export function saveCasaMapaCompareIds(slug, ids) {
  const next = normalizeIdList(ids, CASAMAPA_COMPARE_MAX);
  writeJson(key(slug, "compare"), next);
  emit(slug, { type: "compare", ids: next });
  return next;
}

export function toggleCasaMapaCompare(slug, listingId) {
  const id = String(listingId || "").trim();
  if (!id) {
    return { ids: loadCasaMapaCompareIds(slug), limited: false };
  }
  const current = loadCasaMapaCompareIds(slug);
  if (current.includes(id)) {
    return { ids: saveCasaMapaCompareIds(slug, current.filter((item) => item !== id)), limited: false };
  }
  if (current.length >= CASAMAPA_COMPARE_MAX) {
    return { ids: current, limited: true };
  }
  return { ids: saveCasaMapaCompareIds(slug, [...current, id]), limited: false };
}

export function loadCasaMapaRecentViews(slug) {
  const rows = readJson(key(slug, "recent"), []);
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows
    .filter((row) => row?.id)
    .map((row) => ({ id: String(row.id), viewedAt: row.viewedAt || null }))
    .slice(0, CASAMAPA_RECENT_MAX);
}

export function recordCasaMapaRecentView(slug, listingId) {
  const id = String(listingId || "").trim();
  if (!id) {
    return loadCasaMapaRecentViews(slug);
  }
  const now = new Date().toISOString();
  const next = [
    { id, viewedAt: now },
    ...loadCasaMapaRecentViews(slug).filter((row) => row.id !== id),
  ].slice(0, CASAMAPA_RECENT_MAX);
  writeJson(key(slug, "recent"), next);
  emit(slug, { type: "recent" });
  return next;
}

export function loadCasaMapaPendingSearch(slug) {
  return readJson(key(slug, "pending-search"), null);
}

export function saveCasaMapaPendingSearch(slug, payload) {
  writeJson(key(slug, "pending-search"), payload);
  emit(slug, { type: "pending-search" });
}

export function clearCasaMapaPendingSearch(slug) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(key(slug, "pending-search"));
  } catch {
    /* ignore */
  }
}
